import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Configuration } from 'src/app/app.constants';
import { Observable } from 'rxjs';
import { FilterViewModel } from '../../shared/models/filter/filterViewModel';
import { map } from 'rxjs/operators';
import { IApiService } from '../../shared/interfaces/iHttpService';
import { ValidatedViewModel } from '../../shared/viewModels/validatedViewModel';
import { ModifiableEntityViewModel } from '../../incidents/viewModels/modifiableEntityViewModel';
import { ObjectTypes } from '../../shared/enums/objectTypes';
import { LightFilterUpdateModel } from '../../shared/models/filter/LightFilterUpdateModel';
import { RiskAssesmentListItemViewModel } from '../models/riskAssesmentListItemViewModel';
import { RiskAssesmentDetailsViewModel } from '../models/riskAssesmentDetailsViewModel';
import { StateService } from '../../planning/services/state.service';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { RiskAssessmentOverviewDetailsInEventViewModel } from '../models/RiskAssessmentOverviewDetailsInEventViewModel';
import { WtStorageService } from '../../shared/services/wt-storage.service';
import { AlertService } from '../../shared/services/alert.service';
import { T } from 'src/assets/i18n/translation-keys';
import { TranslateService } from '@ngx-translate/core';
@Injectable({
  providedIn: 'root',
})
export class RiskAssesmentsService extends StateService<RiskAssesmentListItemViewModel> implements IApiService {

  constructor(
    private http: HttpClient,
    private configuration: Configuration,
    private readonly authenticationService: AuthenticationService,
    private readonly wtStorage: WtStorageService,
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService
    ) {
    super();
    
  }

  /**
   * Getter that dynamically builds the actionUrl each time.
   * This ensures the URL updates when the backend API changes (e.g., account switching).
   */
  private get actionUrl(): string {
    return this.configuration.buildEndpoint(`RiskAssesment/`);
  }
  updateLight(item: LightFilterUpdateModel) {
    throw new Error('Method not implemented.');
  }
  list(filters: FilterViewModel[]): Observable<RiskAssesmentListItemViewModel[]> {
    return this.getList(filters);
  }
  pinnedList(
    objectType: ObjectTypes,
    filters: FilterViewModel[],
    showPinnedOnly?: boolean
  ): Observable<ModifiableEntityViewModel[]> {
    throw new Error('Method not implemented.');
  }

  add(risk: RiskAssesmentDetailsViewModel) {
    return this.http.post<ValidatedViewModel>(`${this.actionUrl}Add`, risk);
  }

  copy(originalAsessmentId: number, filters: FilterViewModel[]) {
    return this.http.post<RiskAssesmentDetailsViewModel>(`${this.actionUrl}Copy/${originalAsessmentId}`, filters);
  }

  getList(
    filters: FilterViewModel[],
    showPinnedOnly: boolean = false,
    customSortFilter: number = 0
  ): Observable<RiskAssesmentListItemViewModel[]> {
    return this.http.post<RiskAssesmentListItemViewModel[]>(
      `${this.actionUrl}List/${showPinnedOnly}/${customSortFilter}`,
      filters
    );
  }

  details(riskId: number): Observable<RiskAssesmentDetailsViewModel> {
    return this.http.get<RiskAssesmentDetailsViewModel>(`${this.actionUrl}Details/${riskId}`);
  }

  update(risk: RiskAssesmentDetailsViewModel) {
    return this.http.put<RiskAssesmentDetailsViewModel>(`${this.actionUrl}Update`, risk);
  }

  approve(risk: RiskAssesmentDetailsViewModel) {
    return this.http.put<RiskAssesmentDetailsViewModel>(`${this.actionUrl}Approve`, risk);
  }

  updateLozenges(risk: RiskAssesmentListItemViewModel) {
    return this.http.post(`${this.actionUrl}UpdateLozenges`, risk);
  }

  delete(riskId: number) {
    return this.http.delete(`${this.actionUrl}Delete/${riskId}`);
  }

  archive(riskId: number, archive: boolean) {
    return this.http.post(`${this.actionUrl}Archive/${riskId}/${archive}`, null).pipe(
      map((x) => {
        this.deleteFromList(riskId);
        return x;
      })
    );
  }

  getForMasterRisk(
    filters: FilterViewModel[],
    masterRiskId: number
  ): Observable<RiskAssesmentListItemViewModel[]> {
    return this.http.post<RiskAssesmentListItemViewModel[]>(
      `${this.actionUrl}GetForMasterRisk/${masterRiskId}`,
      filters
    );
  }

  downloadApprovalPDF(id: number) {
  let filename = '';
  const url = this.actionUrl + `DownloadSavedApprovalPDF/${id}`;
  const requestHeaders = new Headers();
  requestHeaders.append('Authorization', 'Bearer ' + this.wtStorage.getItem('access_token'));
  requestHeaders.append('aId', this.authenticationService.getCurrentAccount().id.toString());
  fetch(url, {
    method: 'GET',
    headers: requestHeaders,
    mode: 'cors',
  })
    .then(async (response) => {
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.Message as string);
      }

      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(response.headers.get('Content-Disposition'));

      if (matches !== null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '');
      }

      return response.blob();
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob);
      this.forceDownload(blobUrl, filename);
    })
    .catch((err) => this.alertService.error(err.message as string));
  }

  private forceDownload(blob: string, filename: string) {
    const anchorElement = document.createElement('a');
    anchorElement.download = filename;
    anchorElement.href = blob;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    anchorElement.remove();
    this.alertService.success("Report downloaded", `${filename} has been downloaded.`);
  }
}
